import { useEffect, useState } from "react";
import Button from '@components/legacy/core/Button';
import fetchCustom from "../../../../wp_helpers/fetchCustom";
import getResponseJson from "../../../../wp_helpers/getResponseJson";
import { Card, CardFooter, CardHeader, CardMain, CardMedia } from "../../../Cards";
import { useTranslations } from "../../../context/TranslationContext";
import ModalComponent from "../../../ModalComponent";
import Portrait from "../../../Portrait";
import Video from "../../../Video";
import { useMediaLibrary } from "../../MediaLibraryContext";
import NewVideoQuestionButton from "../NewVideoQuestionButton";

const SCOPE_SHARED = 'shared';
const SCOPE_PERSONAL = 'personal';

const VideoQuestion = ({
    user_photourl: userPhotoUrl,
    user_full_name: userFullName,
    user_first_name: userFirstName,
    user_last_name: userLastName,
    video_question_form_data: videoQuestionFormData,
    created_at: createdAt,
    delete_url: deleteUrl,
    edit_url: editUrl,
    download_url: downloadUrl,
    allow_choose: allowChoose,
    scope,
    can_edit: canEdit,
    scope_url: scopeUrl,
    createVideoQuestionFormApi,
    getVideoQuestions,
}) => {
    const { t } = useTranslations();
    const { handleSelectMedia } = useMediaLibrary();
    const [videoText, setVideoText] = useState('');
    const [deleteBlockedInfo, setDeleteBlockedInfo] = useState(null);
    const {
        title,
        description,
        video_question: videoQuestion = {},
    } = videoQuestionFormData || {};
    const {
        video_url: videoUrl,
        thumbnail_url: thumbnailUrl,
    } = videoQuestion || {};

    const closeDeleteBlockedDialog = () => setDeleteBlockedInfo(null);

    const deleteVideo = async () => {
        const response = await fetchCustom(deleteUrl, {
            method: 'DELETE',
        });
        const responseJson = await getResponseJson(response);
        if (response.ok) {
            getVideoQuestions();
            return;
        }
        if (responseJson && responseJson.referencing_jobs) {
            setDeleteBlockedInfo(responseJson);
        }
    };

    const handleDeleteVideo = async () => {
        window.show_confirmation_overlay(
            'delete-video-question',
            t('Confirmation'),
            t('Are you sure you want to delete this video question?'),
            {},
            {
                label: t('Delete'),
                action: deleteVideo,
            },
        )
    };

    const flipScope = async (nextScope) => {
        const response = await fetchCustom(scopeUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ scope: nextScope }),
        });
        // The toast is getResponseJson's job: it shows the server's own localized message
        // as an error, sticky, so a refusal can be read.
        await getResponseJson(response);
        if (!response.ok) {
            return;
        }
        if (typeof getVideoQuestions === 'function') {
            getVideoQuestions();
        }
    };

    const handleToggleScope = () => {
        if (scope === SCOPE_SHARED) {
            // Going the other way removes it from the Shared Library for everyone else -
            // worth a confirmation. Jobs already using the video keep working either way.
            window.show_confirmation_overlay(
                'flip-video-question-scope',
                t('Confirmation'),
                t('This video will disappear from the Shared Library. Jobs already using it keep working.'),
                {},
                {
                    label: t('Make personal'),
                    action: () => flipScope(SCOPE_PERSONAL),
                },
            );
        } else {
            flipScope(SCOPE_SHARED);
        }
    };

    const handleSelectVideo = async () => {
        handleSelectMedia({ media: videoQuestionFormData });
    };

    useEffect(() => {
        setTimeout(() => {
            if (!videoUrl) {
                setVideoText(t('No video'));
            }
        }, 3000);
    }, []);

    return (
        <Card className="VideoQuestion">
            <CardMedia>
                {videoUrl ? (
                    <Video poster={thumbnailUrl}>
                        <source src={videoUrl} type="video/mp4" />
                    </Video>
                ) : (
                    videoText && <div className="VideoQuestion-video-text">{videoText}</div>
                )}
            </CardMedia>
            <CardHeader>
                <div className="VideoQuestion-author">
                    <h5>
                        <Portrait userPhoto={userPhotoUrl} userName={userFullName} userFirstName={userFirstName} userLastName={userLastName} />
                        <span className="VideoQuestion-author-name">{userFullName}</span>
                    </h5>
                </div>
                <div className="VideoQuestion-date">{localizedDate(createdAt)}</div>
            </CardHeader>
            <CardMain>
                {scope &&
                    <span className={`label ${scope === SCOPE_SHARED ? 'label-primary' : 'label-dark'} VideoQuestion-scope-badge`}>
                        {scope === SCOPE_SHARED ? t('Shared') : t('Personal')}
                    </span>
                }
                {title && <h6>{title}</h6>}
                {description}
            </CardMain>
            {(deleteUrl || editUrl || downloadUrl || allowChoose || (canEdit && scopeUrl)) &&
                <CardFooter>
                    <div className="btn-container">
                        {editUrl && createVideoQuestionFormApi &&
                            <NewVideoQuestionButton
                                mode="edit"
                                createVideoQuestionFormApi={createVideoQuestionFormApi}
                                editUrl={editUrl}
                                prefillFormData={videoQuestionFormData}
                                getVideoQuestions={getVideoQuestions}
                            />
                        }
                        {canEdit && scopeUrl &&
                            <Button type="link-secondary" onClick={handleToggleScope}>
                                {scope === SCOPE_SHARED ? t('Make personal') : t('Share with the whole company')}
                            </Button>
                        }
                        {deleteUrl && <Button type="link-secondary" onClick={handleDeleteVideo}>{t('Delete')}</Button>}
                        {downloadUrl && <Button type="link-secondary" href={downloadUrl}>{t('Download')}</Button>}
                    </div>
                    <div className="btn-container">
                        {allowChoose && <Button type="primary" onClick={handleSelectVideo}>{t('Select')}</Button>}
                    </div>
                </CardFooter>
            }
            <ModalComponent show={Boolean(deleteBlockedInfo)} closeModal={closeDeleteBlockedDialog}>
                <div className="overlay">
                    <div className="overlay-header">
                        <h3>{t('This video question cannot be deleted')}</h3>
                    </div>
                    <div className="overlay-main">
                        <p>{deleteBlockedInfo?.message || t('This video question is used by one or more job postings and cannot be deleted.')}</p>
                        {deleteBlockedInfo?.referencing_jobs?.length > 0 &&
                            <ul>
                                {deleteBlockedInfo.referencing_jobs.map((job) => (
                                    <li key={job.id}>{job.name}</li>
                                ))}
                            </ul>
                        }
                        {deleteBlockedInfo?.referencing_jobs?.length > 0 && deleteBlockedInfo.referencing_jobs_partially_hidden &&
                            <p>{t('Plus other job postings you do not have access to view.')}</p>
                        }
                    </div>
                    <div className="overlay-footer">
                        <div className="btn-container">
                            <Button type="primary" onClick={closeDeleteBlockedDialog}>{t('OK')}</Button>
                        </div>
                    </div>
                </div>
            </ModalComponent>
        </Card>
    );
};

export default VideoQuestion;
